import { useCallback, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { Button, tokens, Text, Link } from '@hotjar/hotsauce';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import { SubtitleContainer, Wrapper } from './styles';
import { sessionsSettingsApi } from '@hotjar/webapp/features/Settings/api';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';

type ErrorCapturingDisabledEmptyStateParams = {
  hasSessionSettingsUpdatePermission: boolean;
  siteId: number;
};

const AdminInfoSubtitle = styled(Text)`
  max-width: 450px;
  margin-bottom: ${tokens.spacer.x3};
  color: ${tokens.color.text_disabled};
`;

const CapturingEnabledSubtitle = styled(Text)`
  white-space: pre-line;
`;

export const StyledLink = styled(Link)`
  color: ${tokens.color.text_default};
  text-decoration: underline;
`;

export const ErrorCapturingDisabledEmptyState = ({
  hasSessionSettingsUpdatePermission,
  siteId,
}: ErrorCapturingDisabledEmptyStateParams): JSX.Element => {
  const { t } = useTranslation();
  const { success, error } = useNotify();
  const [userHasEnabledCapturing, setUserHasEnabledCapturing] = useState(false);
  const siteSettingsUrl = `/site/${siteId}/settings#targeting`;

  // @TODO Replace it with packages/recordings/src/hooks/useEnableSessionCapture.ts
  const onSubmit = useCallback(async () => {
    const response = await sessionsSettingsApi.post(siteId as number, 'update_session_settings', {
      session_capture_console_consent: true,
    });

    if (response.success) {
      success(
        t('common:recordings.list.error-capturing-disabled.console-tracking-enabled-success'),
      );
      setUserHasEnabledCapturing(true);
      return;
    }

    error(t('common:recordings.list.error-capturing-disabled.console-tracking-enabled-error'));
  }, [siteId, error, t, success]);

  // TODO: Replace with shared component once RECS-1555 is done
  if (userHasEnabledCapturing) {
    return (
      <Wrapper>
        <EmptyState
          icon="check"
          iconColor="text_success"
          title={t(
            'common:recordings.list.error-capturing-disabled.console-tracking-enabled-title',
          )}
          subtitle={
            <CapturingEnabledSubtitle>
              <Trans
                i18nKey="common:recordings.list.error-capturing-disabled.console-tracking-enabled-subtitle"
                components={[
                  // eslint-disable-next-line react/jsx-key
                  <StyledLink kind="inline" isExternal href={siteSettingsUrl}>
                    site settings
                  </StyledLink>,
                ]}
              />
            </CapturingEnabledSubtitle>
          }
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <EmptyState
        icon="warning_alt"
        iconColor="text_default"
        title={t('common:recordings.list.error-capturing-disabled.title')}
        subtitle={
          <SubtitleContainer>
            {t('common:recordings.list.error-capturing-disabled.suggestion')}
          </SubtitleContainer>
        }
      />
      {hasSessionSettingsUpdatePermission ? (
        <Button icon="code_tracking" as="button" onClick={onSubmit} kind="secondary">
          {t('common:recordings.list.error-capturing-disabled.button')}
        </Button>
      ) : (
        <AdminInfoSubtitle textType="body2">
          {t('common:recordings.list.error-capturing-disabled.admin-info')}
        </AdminInfoSubtitle>
      )}
    </Wrapper>
  );
};
